import { getPointsByType } from './equipment';
import { parseStringToInt } from '../../utils/parsing';

const getPointsByPointType = (state, searchParams) => {
  const ptid = parseInt(searchParams.ptid, 10);
  const pointsByType = getPointsByType(state, searchParams);
  const pointType = pointsByType.filter((pt) => pt.pointTypeId === ptid);
  if (pointType && pointType.length === 1) {
    const points = pointType[0].points.map((p) => ({
      id: p.pointId,
      ...p,
    }));
    return points;
  }

  return [];
};

export const getPointName = (state, pointId) => {
  let name = '';
  if (!state || !pointId) return name;
  const pointsById = state.entities.points.byId;
  const equipById = state.entities.equipment.byId;
  // eslint-disable-next-line no-prototype-builtins
  if (pointsById.hasOwnProperty(pointId)) {
    const point = pointsById[pointId];
    const { equipId } = point;
    // eslint-disable-next-line no-prototype-builtins
    if (equipId && equipById.hasOwnProperty(equipId)) {
      const equip = equipById[equipId];
      name = `${equip.typeAbbr}-${equip.suffix} - `;
    }
    name += point.type;
  }
  return name;
};

const valueMappingToArray = (valueMappings) => {
  const keys = Object.keys(valueMappings);
  if (keys.length === 0) return [];
  // failsafe for 0 and 1-indexed mappings, make sure the 0th pos is always populated
  const arr = [''];
  keys
    .map(parseStringToInt)
    .filter((key) => key != null)
    .forEach((key) => {
      arr[key] = valueMappings[key];
    });
  return arr;
};

const attachDefaultUnit = (state, point) => {
  const {
    entities: {
      pointTypes: { byId: ptById },
      measurements: { byId: measurementsById },
      units: { byId: unitsById },
    },
  } = state;
  const pt = ptById[point.pointTypeId];
  const measurement = measurementsById[pt?.measurementId];
  // almost all measurements are convertible, default to true here
  const isConvertible = measurement == null ? true : measurement.is_convertible;
  const unitId = isConvertible ? pt?.defaultUnitId : point.rawUnitId;
  return {
    ...point,
    // eslint-disable-next-line camelcase
    defaultUnit: unitsById[unitId]?.name_abbr,
  };
};

// attach the point type's default unit to each point
// this is the unit which ingested is converted into (and what influx returns)
export const attachDefaultUnits = (state, points) =>
  points.map((point) => attachDefaultUnit(state, point));

const getDataMapping = (state, point, series) => {
  // the series contains a more precise answer about what the point is currently being
  // converted to, use it if available
  // otherwise assume the "default conversion" which is based on the point type/measurement
  const targetUnit = series?.unit || point.defaultUnit;
  // for points that aren't converted from Ordinal the mapping is unique per-point
  if (targetUnit === 'Ordinal') {
    return point.dataMapping || {};
  }
  // for points which are converted to binary or enum data points the mapping
  // is defined by the target (default) unit
  const unit = state.entities.units.byName[targetUnit];
  // eslint-disable-next-line camelcase
  const dataType = unit?.data_type;
  if (dataType === 'Binary' || dataType === 'Enum') {
    return unit.display_encoding;
  }
  return {};
};

/**
 * If a point has a 'dataMapping' defined than we know its integer values are encodings
 * mapping to a set of string values. We'll set up a new mapping axis for each unique
 * encoding encountered and return a map of id -> mapping so that the charting lib can
 * display points with their encodings.
 *
 * Each mapping defines a separate y-axis of a graph, where the empty mapping '{}' indicates
 * that the axis is numerical / not transformed. We also return a map of point id -> the index
 * of the axis it should be graphed on
 */
export const getPointMappings = (state, pointIds) => {
  const {
    entities: {
      points: { byId: pointsById, units },
      units: { byName },
      series: { byId: seriesById },
    },
  } = state;
  const mappings = [];
  const pointIdToAxis = {};
  const seriesToPos = {};
  pointIds.forEach((pid) => {
    const point = attachDefaultUnit(state, pointsById[pid]);
    const valueMapping = getDataMapping(state, point, seriesById[pid]);
    const key = JSON.stringify(valueMapping);
    if (seriesToPos[key] == null) {
      mappings.push({
        data: valueMappingToArray(valueMapping),
        unit: byName[units[pid]]?.name_long || '...',
      });
      seriesToPos[key] = mappings.length - 1;
    }
    const pos = seriesToPos[key];
    pointIdToAxis[pid] = pos;
  });
  return {
    mappings,
    pointIdToAxis,
  };
};

export default getPointsByPointType;
